import { UUID } from 'crypto';
import { isDateBefore, isDateSame, isDateSameOrAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { TankTypeEnum } from 'src/types/ViewModel/AolEnums';
import { AolDataItem, AolGroup } from 'src/types/ViewModel/AolItem';
import { AppFeatureVessel } from 'src/types/ViewModel/AppFeature/AppFeatureVessel';
import { BiofuelGrade, getBiofuelGrade } from 'src/types/ViewModel/Bunkering/BiofuelGrade';
import { BioFuelType } from 'src/types/ViewModel/Bunkering/BioFuelType';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent, BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkeringStatus } from 'src/types/ViewModel/Bunkering/BunkeringStatus';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { FuelRobVM } from 'src/types/ViewModel/Bunkering/FuelRobVM';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { ProofOfSustainabilityEntry } from 'src/types/ViewModel/Bunkering/ProofOfSustainabilityEntry';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { BUNKERING_INPUT_FIELD_KEYS } from 'src/enums/bunkering/bunkeringInputFieldKeys';

export const BIO_DEFAULT_DENSITY = 0.8919;

export enum BunkeringPageTabs {
    registration = 0,
    rob = 1,
}

export enum BunkerRegTabsEnum {
    fuelReceived = 0,
    status = 1,
}

export const fuelTankTypes: number[] = [
    TankTypeEnum.HeavyFuelOil,
    TankTypeEnum.MarineGasOil,
    TankTypeEnum.LightFuelOil,
    TankTypeEnum.VirtualHFOTank,
    TankTypeEnum.VirtualMGOTank,
];

export const getFuelTanks = (vessel: Vessel): Tank[] => {
    return vessel?.nonCargoTanks?.filter((tank) => fuelTankTypes.includes(tank.tankType));
};

export const getBunkeringReference = (
    fuelType: string,
    portUnCode: string | null,
    portName: string | null,
    bunkeringDateTime: Date,
    index?: number | null
): string => {
    return `${fuelType} ${portUnCode ?? portName?.substring(0, 3) ?? 'UNKNOWN'} ${bunkeringDateTime.getFullYear()}-${index ?? 1}`;
};

export const getBunkeringReferenceIndex = (bunkeringReference?: string | null): number | null => {
    if (!bunkeringReference) return null;
    const match = bunkeringReference.match(/-(\d+)$/);
    if (!match) return null;

    const index = Number(match[1]);
    return Number.isInteger(index) && index > 0 ? index : null;
};

export const getMaxBunkeringReferenceIndex = (fuelType: FuelType, bunkeringDateTime: Date, bunkerings: Bunkering[]): number => {
    const year = bunkeringDateTime.getFullYear();

    return bunkerings
        .filter((bunkering) => !bunkering.deleted && bunkering.fuelType === fuelType && bunkering.bunkeringDateTime?.getFullYear() === year)
        .reduce((maxIndex, bunkering) => {
            const index = getBunkeringReferenceIndex(bunkering.bunkeringReference);
            return index && index > maxIndex ? index : maxIndex;
        }, 0);
};

export const hasBunkeringRemainingOnBoard = (currentStatus: BunkeringStatus): boolean => {
    return currentStatus.status !== BunkeringStatusEnum.Debunkered && currentStatus.status !== BunkeringStatusEnum.Spent;
};

export const getLatestStatus = (statuses: BunkeringStatus[]): BunkeringStatus | null => {
    return statuses.length > 0 ? statuses.reduce((prev, current) => (prev.activeFromDate > current.activeFromDate ? prev : current)) : null;
};

export const getBunkeringStatusAtDate = (bunkering: Bunkering, date: Date): BunkeringStatus | null => {
    if (!bunkering.bunkeringStatuses || bunkering.bunkeringStatuses.length === 0) return null;
    const statusesBeforeDate = bunkering.bunkeringStatuses.filter((status) => status.activeFromDate <= date);
    const latestStatus = statusesBeforeDate.reduce((prev, current) => (prev?.activeFromDate > current?.activeFromDate ? prev : current), null);
    return latestStatus;
};

export const isBunkeringOnboardAtDate = (bunkering: Bunkering, date: Date): boolean => {
    const status = getBunkeringStatusAtDate(bunkering, date);
    if (!status && isDateSameOrBefore(bunkering.bunkeringDateTime, date)) return true;
    return status && status.status !== BunkeringStatusEnum.Unknown && hasBunkeringRemainingOnBoard(status);
};

export const isBunkeringUsableAtDate = (bunkering: Bunkering, date: Date): boolean => {
    const status = getBunkeringStatusAtDate(bunkering, date);
    return status && (status.status === BunkeringStatusEnum.Approved || status.status === BunkeringStatusEnum.InUse);
};

export const getBunkeringsOnboardAtDate = (bunkerings: Bunkering[], date: Date): Bunkering[] => {
    return bunkerings.filter((bunkering) => isBunkeringOnboardAtDate(bunkering, date));
};

const getDateSpanForAolGroup = (date: Date): { fromDate: Date; toDate: Date } => {
    const fromDate = new Date(date);
    fromDate.setDate(fromDate.getDate() - 1);
    fromDate.setHours(12, 0, 0, 0);
    const toDate = new Date(date);
    toDate.setHours(12, 0, 0, 0);
    return { fromDate, toDate };
};

export const getBunkeringEventsInAolGroupPeriod = (date: Date, bunkeringEvents: BunkeringEvent[]) => {
    const { fromDate, toDate } = getDateSpanForAolGroup(date);
    return bunkeringEvents.filter(
        (bunkeringEvent) =>
            isDateSameOrAfter(bunkeringEvent.bunkeringDateTimeLocal, fromDate) && isDateBefore(bunkeringEvent.bunkeringDateTimeLocal, toDate)
    );
};

export const getBunkeringEventsInAolPeriod = (fromDate: Date, toDate: Date, bunkeringEvents: BunkeringEvent[]) => {
    return bunkeringEvents.filter(
        (bunkeringEvent) =>
            isDateSameOrAfter(bunkeringEvent.bunkeringDateTimeLocal, fromDate) && isDateBefore(bunkeringEvent.bunkeringDateTimeLocal, toDate)
    );
};

export const getBunkeringsInAolGroup = (date: Date, bunkerings: Bunkering[]): Bunkering[] => {
    const { fromDate, toDate } = getDateSpanForAolGroup(date);
    return bunkerings?.filter(
        (bunkering) => isDateSameOrAfter(bunkering.bunkeringDateTime, fromDate) && isDateBefore(bunkering.bunkeringDateTime, toDate)
    );
};

export const getBunkeringsInAolPeriod = (fromDate: Date, toDate: Date, bunkerings: Bunkering[]): Bunkering[] => {
    return bunkerings?.filter(
        (bunkering) => isDateSameOrAfter(bunkering.bunkeringDateTime, fromDate) && isDateBefore(bunkering.bunkeringDateTime, toDate)
    );
};

export const getPreviousAolItem = (currentAolItem: AolDataItem, aolItems: AolDataItem[]): AolDataItem | null => {
    const currentIndex = aolItems
        .sort((a, b) => (isDateSameOrBefore(a.reportToTimeLocalTime, b.reportToTimeLocalTime) ? -1 : 1))
        .findIndex((item) => item.id && item?.id === currentAolItem?.id);
    return currentIndex > 0
        ? aolItems.sort((a, b) => (isDateSameOrBefore(a.reportToTimeLocalTime, b.reportToTimeLocalTime) ? -1 : 1))[currentIndex - 1]
        : null;
};

const check = (bunkering: Bunkering, tankId: number, previousRobs: FuelROB[], currentRobs: FuelROB[], currentBunkerings: Bunkering[]): boolean => {
    // an ROB exists for the tank and the ROB is greater than 0 in previous ROBs
    const inPreviousRob = previousRobs?.some((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid && rob.tankId === tankId && rob.rob > 0);
    // fuel was transfered in this Aol period so that it is in the tank for current period, but not previous
    const inCurrentRob = currentRobs?.some((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid && rob.tankId === tankId && rob.rob > 0);
    // or the fuel was bunkered in the current Aol period
    const newBunkering = currentBunkerings.some(
        (b) => b.bunkeringGuid === bunkering.bunkeringGuid && b.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId)
    );

    return inPreviousRob || inCurrentRob || newBunkering;
};

export const getContentsInTankAtDate = (
    bunkerings: Bunkering[],
    tankId: number,
    date: Date,
    aolGroup: AolGroup,
    allAolDataItems: AolDataItem[],
    robs: FuelROB[],
    appFeatureVessel?: AppFeatureVessel | null
): Bunkering[] => {
    if (aolGroup?.aolDataItems?.length > 0) {
        const firstAolItem = aolGroup.aolDataItems[0];
        const noonReportIds = aolGroup.aolDataItems.map((item) => item.noonReport?.metadata?.noonReportId).filter((id) => id !== undefined);
        const previousAolItem = getPreviousAolItem(firstAolItem, allAolDataItems);
        const previousRobs = robs?.filter((rob) => rob.noonReportId && rob.noonReportId === previousAolItem?.noonReport?.metadata?.noonReportId);
        const currentRobs = robs?.filter((rob) => rob.noonReportId && noonReportIds.includes(rob.noonReportId));
        const currentBunkerings = getBunkeringsInAolGroup(date, bunkerings);
        if (appFeatureVessel && appFeatureVessel.fromDate && isDateSame(appFeatureVessel.fromDate, date, 'day')) {
            return bunkerings.filter(
                (bunkering) =>
                    // filter out bunkering events which are registered as spent and were bunkered before the feature activation date
                    !(
                        getBunkeringStatusAtDate(bunkering, date)?.status === BunkeringStatusEnum.Spent &&
                        isDateBefore(bunkering.bunkeringDateTime, appFeatureVessel.fromDate)
                    ) &&
                    isDateSameOrBefore(bunkering.bunkeringDateTime, date) &&
                    (bunkering.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId) ||
                        check(bunkering, tankId, previousRobs, currentRobs, currentBunkerings))
            );
        }

        const _bunkerings = bunkerings.filter((bunkering) => check(bunkering, tankId, previousRobs, currentRobs, currentBunkerings));

        return _bunkerings;
    }
    return bunkerings.filter(
        (bunkering) => isBunkeringOnboardAtDate(bunkering, date) && bunkering.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId)
    );
};

export const getTankTypeDisplayName = (tankType: TankTypeEnum): string => {
    switch (tankType) {
        case TankTypeEnum.Unknown:
            return 'Unknown';
        case TankTypeEnum.Cargo:
            return 'Cargo';
        case TankTypeEnum.WaterBallast:
            return 'Water Ballast';
        case TankTypeEnum.HeavyFuelOil:
            return 'Heavy Fuel Oil';
        case TankTypeEnum.MarineGasOil:
            return 'Marine Gas Oil';
        case TankTypeEnum.LubOil:
            return 'Lubricating Oil';
        case TankTypeEnum.FreshWater:
            return 'Fresh Water';
        case TankTypeEnum.TechnicalFreshWater:
            return 'Technical Fresh Water';
        case TankTypeEnum.MiscellaneousTanks:
            return 'Miscellaneous Tanks';
        case TankTypeEnum.LightFuelOil:
            return 'Light Fuel Oil';
        case TankTypeEnum.VirtualHFOTank:
            return 'Virtual HFO Tank';
        case TankTypeEnum.VirtualMGOTank:
            return 'Virtual MGO Tank';
        default:
            return '';
    }
};

export enum BunkerRegSectionType {
    OperationalData = 1,
    BDN = 2,
    FTA = 3,
    ProofOfSustainability = 4,
}

export interface BunkeringInputField {
    key: string;
    label: string;
    bioFuel: boolean;
    inputType: string;
    section: BunkerRegSectionType;
    min?: number;
    max?: number;
    unit?: string;
    options?: string[];
    selectDefaultOption?: string;
}

const BIO_BLEND_FOSSIL_FUEL_TYPE_OPTIONS = [FuelType[FuelType.LSF], FuelType[FuelType.LGO]];

export const bunkeringInputFields: BunkeringInputField[] = [
    { key: BUNKERING_INPUT_FIELD_KEYS.BDN_REFERENCE, label: 'BDN Reference', bioFuel: false, inputType: 'text', section: BunkerRegSectionType.BDN },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE,
        label: 'Fuel Type',
        bioFuel: false,
        inputType: 'select',
        section: BunkerRegSectionType.OperationalData,
        options: Object.keys(FuelType).filter((v) => isNaN(Number(v))),
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.TOTAL_RECEIVED_QUANTITY,
        label: 'Total Received Quantity',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0,
        max: 3000,
        unit: 'MT',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.SULPHUR,
        label: 'Sulphur',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0,
        max: 10,
        unit: '%',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FUEL_VISCOSITY,
        label: 'Fuel Viscosity',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0.5,
        max: 500,
        unit: 'mm²/s @40°C/50°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BDN_LITER_WEIGHT,
        label: 'BDN Liter Weight',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0.5,
        max: 1.5,
        unit: 'kg/L @15°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FTA_LITER_WEIGHT,
        label: 'FTA Liter Weight',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0.5,
        max: 1.5,
        unit: 'kg/L @15°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.WATER_CONTENT,
        label: 'Water Content',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0,
        max: 50,
        unit: '%',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIO_FUEL_TYPE,
        label: 'Biofuel Type',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: Object.keys(BioFuelType).filter((v) => isNaN(Number(v))),
        selectDefaultOption: 'Select biofuel type',
    },
    /**
     * This field has been deactivated and functionally replaced by biofuelGrade
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.BIO_BLEND,
    //     label: 'Bio Blend',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.BDN,
    //     min: 0,
    //     max: 100,
    //     unit: '%',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_VOLUME,
        label: 'Volume Biofuel',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 0,
        max: 3000,
        unit: 'm³',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS,
        label: 'Biofuel quantity (portion of blend)',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 0,
        max: 3000,
        unit: 'MT',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_ENERGY_CONTENT,
        label: 'Bio Energy Content',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        unit: 'MJ',
        min: 1,
        max: 30000000,
    },
    /**
     * This field has been deactivated until further notice (IMO regulations may come in 2026 or later)
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.LOWER_HEATING_VALUE,
    //     label: 'IMO Lower Heating Value',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.ProofOfSustainability,
    //     min: 20,
    //     max: 60,
    //     unit: 'MJ/kg',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.EU_LOWER_CALORIFIC_VALUE,
        label: 'EU Lower Calorific Value',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 20,
        max: 60,
        unit: 'MJ/kg',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.EU_GHG_EMISSIONS,
        label: 'EU GHG Emissions intensity',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 1,
        max: 40,
        unit: 'gCO₂eq/MJ',
    },
    /**
     * This field has been deactivated until further notice (IMO regulations may come in 2026 or
     * later)
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.IMO_GHG_EMISSIONS,
    //     label: 'IMO GHG Emissions intensity',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.ProofOfSustainability,
    //     min: 1,
    //     max: 99,
    //     unit: 'gCO₂eq/MJ',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.SUSTAINABILITY_REFERENCE,
        label: 'Sustainability Reference',
        bioFuel: true,
        inputType: 'text',
        section: BunkerRegSectionType.ProofOfSustainability,
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_GRADE,
        label: 'Biofuel Grade',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: Object.keys(BiofuelGrade).filter((v) => isNaN(Number(v))),
        selectDefaultOption: 'Select biofuel grade',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIO_BLEND_FOSSIL_FUEL_TYPE,
        label: 'Bio Blend Fossil Fuel Type',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: BIO_BLEND_FOSSIL_FUEL_TYPE_OPTIONS,
        selectDefaultOption: 'Select fossil fuel type',
    },
];

export const getBunkeringStatusEnumDisplayName = (status: BunkeringStatusEnum, statusActiveFrom: Date, approvedForUseAt: Date | null): string => {
    switch (status) {
        case BunkeringStatusEnum.Unknown:
            return 'New';
        case BunkeringStatusEnum.Registered:
            return 'Awaiting approval';
        case BunkeringStatusEnum.Approved:
            return 'Approved';
        case BunkeringStatusEnum.Rejected:
            return 'Rejected';
        case BunkeringStatusEnum.InUse:
            return !approvedForUseAt || statusActiveFrom < approvedForUseAt ? 'Approval bypassed' : 'In Use';
        case BunkeringStatusEnum.Spent:
            return 'Spent';
        case BunkeringStatusEnum.Debunkered:
            return 'Debunkered';
        default:
            return 'New';
    }
};

export const getOneYearBefore = (date: Date): Date => {
    const oneYearBefore = new Date(date);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);
    return oneYearBefore;
};

export const getThreeYearsBefore = (date: Date): Date => {
    const threeYearsBefore = new Date(date);
    threeYearsBefore.setFullYear(threeYearsBefore.getFullYear() - 3);
    return threeYearsBefore;
};

export const checkBunkeringStatusAfterRobUpdate = (bunkering: Bunkering, fuelRobData: FuelROB[]): BunkeringStatusEnum => {
    const allRobData = fuelRobData.filter((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid);
    if (!allRobData || !allRobData.some((rob) => rob.rob > 0)) {
        return BunkeringStatusEnum.Spent;
    } else if (allRobData.some((rob) => rob.consumed > 0)) return BunkeringStatusEnum.InUse;
    return bunkering.currentStatus.status;
};

export const getAolGroupForDateTime = (date: Date, aolGroups: AolGroup[]): AolGroup | null => {
    return aolGroups.find((aolGroup) => isDateSameOrAfter(aolGroup.aolDate, date)) || null;
};

export const getMaxWeight = (tank: Tank, literWeight: number) => {
    const maxWeight = tank.volumeAt98Percent * (literWeight ?? 1);
    return Number(maxWeight.toFixed(3));
};

export const getNumUnregisteredBunkeringEvents = (
    bunkeringEvents: BunkeringEvent[],
    bunkerings: Bunkering[],
    selectedBunkeringDate: Date
): [number, number] => {
    if (bunkerings && bunkeringEvents) {
        const _bunkeringEvents = bunkeringEvents.filter(
            (b) =>
                isDateSameOrBefore(b.bunkeringDateTimeLocal, selectedBunkeringDate, 'day') &&
                isDateSameOrAfter(b.bunkeringDateTimeLocal, getOneYearBefore(selectedBunkeringDate))
        );
        const _numUnregisteredBunkerings = _bunkeringEvents.filter(
            (b) => !b.debunkering && !bunkerings.find((b2) => b2.eventId === b.eventID)
        ).length;
        const _numUnregisteredDebunkerings = _bunkeringEvents.filter(
            (b) => b.debunkering && !bunkerings.find((b2) => b2.debunkeringEventIds.includes(b.eventID as UUID))
        ).length;
        return [_numUnregisteredBunkerings, _numUnregisteredDebunkerings];
    }
    return [0, 0];
};

export const getBunkeringForBunkeringEvent = (bunkeringEvent: BunkeringEvent, bunkerings: Bunkering[]): Bunkering | null => {
    return bunkerings.find(
        (b) =>
            b.eventId === bunkeringEvent.eventID &&
            (b.fuelType === bunkeringEvent.fuelType || (b.fuelType === FuelType.LSF && bunkeringEvent.fuelType === FuelType.HFO))
    );
};

export const canUserChangeStatus = (currentBunkeringStatus: BunkeringStatus): boolean => {
    return currentBunkeringStatus?.status !== BunkeringStatusEnum.Debunkered && currentBunkeringStatus?.status !== BunkeringStatusEnum.Spent;
};

export const validateTotalReceivedVsAllocatedFuel = (bunkering: Bunkering): boolean => {
    if (bunkering?.totalReceivedQuantity === null || bunkering?.totalReceivedQuantity === undefined || bunkering?.totalReceivedQuantity === 0) {
        return true;
    }
    const totalAllocatedToTanks = parseFloat(bunkering.bunkerReceivedPerTanks.reduce((acc, tank) => acc + tank.bunkerReceivedMt, 0).toFixed(2));

    return totalAllocatedToTanks === parseFloat(bunkering.totalReceivedQuantity?.toFixed(2));
};

export const getBiofuelGradeWarningText = (
    biofuelGrade: BiofuelGrade | undefined,
    proofOfSustainabilityEntries: ProofOfSustainabilityEntry[] | undefined,
    totalReceivedQuantity: number | undefined
): string => {
    if (
        !biofuelGrade ||
        typeof totalReceivedQuantity !== 'number' ||
        !Number.isFinite(totalReceivedQuantity) ||
        totalReceivedQuantity <= 0 ||
        !proofOfSustainabilityEntries?.length
    ) {
        return '';
    }

    const gradeLabel = typeof biofuelGrade === 'number' ? getBiofuelGrade(biofuelGrade) : String(biofuelGrade);
    if (!gradeLabel) return '';
    const expectedShare = /^B(10|20|24|30|50|100)$/.test(gradeLabel) ? Number(gradeLabel.slice(1)) : null;
    if (expectedShare === null) return '';

    const biofuelMasses = proofOfSustainabilityEntries
        .map((entry) => entry.biofuelMass)
        .filter((mass): mass is number => mass !== undefined && mass !== null);
    if (biofuelMasses.length === 0 || biofuelMasses.some((mass) => !Number.isFinite(mass) || mass < 0)) return '';

    const totalBiofuelMass = biofuelMasses.reduce((sum, mass) => sum + mass, 0);
    if (totalBiofuelMass <= 0) return '';

    const actualShare = (totalBiofuelMass / totalReceivedQuantity) * 100;
    const minShare = expectedShare === 100 ? 95 : expectedShare - 5;
    const maxShare = expectedShare === 100 ? 100 : expectedShare + 5;
    if (actualShare >= minShare && actualShare <= maxShare) return '';

    return `Combined POS biofuel quantity is ${actualShare.toFixed(2)}% of total received quantity, outside the accepted range for ${gradeLabel} (${minShare}-${maxShare}%).`;
};

export const hasConsOrLossForTankAndBunkering = (tankId: number, bunkeringGuid: UUID, fuelrobVM: FuelRobVM): boolean => {
    return fuelrobVM.fuelRobColumns.some((c) =>
        c.fuelRobs.some((f) => f.bunkeringGuid === bunkeringGuid && f.tankId === tankId && (f.consumed > 0 || f.loss < 0))
    );
};

/**
 * A fixed set of shades per fuel type ("color family"), used to visually tag a bunkering operation on the ROB tab
 * (contents cell swatch and the "ROB per bunkering reference" legend). Every bunkering of a given fuel type is
 * colored from that fuel type's own family, so the fuel type is recognizable by hue at a glance, while the
 * specific shade distinguishes individual bunkerings of that same fuel type from one another (e.g. two different
 * HFO bunkerings comingled in one tank).
 */
export const FUEL_TYPE_COLOR_FAMILIES: Record<FuelType, string[]> = {
    [FuelType.HFO]: ['#050505', '#444444', '#808080', '#C7C7C7'],
    [FuelType.LGO]: ['#704500', '#C88400', '#FFD000', '#FFF1A6'],
    [FuelType.BIO]: ['#310057', '#6915A3', '#AC3DE0', '#E5C0FA'],
    [FuelType.LSF]: ['#324A57', '#607D8B', '#9FB7C2', '#E4ECEF'],
};

/** Used for any fuel type outside FUEL_TYPE_COLOR_FAMILIES (legacy/unset data), so such a bunkering still gets a family. */
export const FALLBACK_BUNKER_COLOR_FAMILY: string[] = ['#8B0000', '#B22222', '#CD5C5C', '#F08080'];

/** The color family a bunkering's shade is picked from, based on its fuel type. */
const getColorFamily = (fuelType?: FuelType | number | null): string[] =>
    FUEL_TYPE_COLOR_FAMILIES[fuelType as FuelType] ?? FALLBACK_BUNKER_COLOR_FAMILY;

/**
 * Deterministic 32-bit FNV-1a hash of a bunkeringGuid. Used to derive a bunkering's *preferred* family slot so
 * that the same bunkering keeps the same shade for every user, on every login, regardless of which other
 * bunkerings happen to be loaded into BunkerContext (that set varies per user, since the bunkerings fetch window
 * is derived from the user's own AoL period/filter).
 */
const hashBunkeringGuid = (bunkeringGuid: string): number => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < bunkeringGuid.length; i++) {
        hash ^= bunkeringGuid.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return Math.abs(hash);
};

/**
 * A bunkering's *preferred* slot within its fuel type's color family (see getColorFamily).
 *
 * Uses `colorIndex` when present: the backend allocates it once on insert (BunkerUtils.AllocateColorIndex, applied
 * only when the bunkering doesn't already exist) and never updates it, so it is a persisted, server-authoritative
 * value that is identical for every user on every login.
 *
 * Falls back, for rows predating that column, to the trailing index of `bunkeringReference`
 * (e.g. 'HFO NLRTM 2026-12' -> 12), which the backend likewise allocates once on insert, per fuel type, and never
 * mutates - so consecutive bunkerings of the same fuel type naturally cycle through their family. Finally falls
 * back to a hash of the guid when the reference carries no parseable index (legacy or hand-edited data), so every
 * bunkering still gets a stable shade.
 */
export const getPreferredFamilySlot = (
    bunkering: Pick<Bunkering, 'bunkeringGuid' | 'bunkeringReference' | 'fuelType'> & { colorIndex?: number | null }
): number => {
    const familyLength = getColorFamily(bunkering?.fuelType).length;
    const colorIndex = bunkering?.colorIndex;
    if (typeof colorIndex === 'number' && Number.isFinite(colorIndex) && colorIndex >= 0) {
        return colorIndex % familyLength;
    }
    const referenceIndex = getBunkeringReferenceIndex(bunkering?.bunkeringReference);
    if (referenceIndex === null) {
        return hashBunkeringGuid(bunkering?.bunkeringGuid ?? '') % familyLength;
    }
    // referenceIndex is 1-based and already scoped to this fuel type (allocated per imo/fuelType/year), so it
    // cycles directly through the family without needing any further stagger.
    return (referenceIndex - 1) % familyLength;
};

/**
 * Collects the bunkeringGuids actually rendered in the ROB table, i.e. those present in the visible fuel ROB
 * columns. This is the correct input for getBunkeringColorMap: it depends only on the vessel/date range being
 * viewed, so two different users looking at the same ROB view derive the same set (and therefore the same
 * colors), unlike the full BunkerContext `bunkerings` array whose size depends on the individual user's AoL
 * filter.
 */
export const getVisibleBunkeringGuids = (fuelRobVM: FuelRobVM): string[] => {
    const guids = (fuelRobVM?.fuelRobColumns ?? []).flatMap((column) => (column?.fuelRobs ?? []).map((rob) => rob.bunkeringGuid));
    return Array.from(new Set(guids.filter((guid) => !!guid)));
};

/**
 * Builds a bunkeringGuid -> color map, combining two properties that no single strategy provides alone:
 *
 *  1. Stability - a bunkering's preferred slot within its fuel type's family is derived from its persisted,
 *     server-allocated reference index (see getPreferredFamilySlot), so it keeps the same shade across logins,
 *     across users and when the surrounding set of bunkerings changes. Assigning by position in the loaded array
 *     would fail here: adding, deleting or scrolling past one older bunkering shifts every later index and
 *     recolors the whole table.
 *  2. Distinctness - where two *visible* bunkerings of the *same fuel type* want the same slot, one keeps it and
 *     the other probes forward to the next free slot in that family, so shades stay unique among the bunkerings
 *     of that fuel type on screen (up to that family's length of them). That is what makes a comingled tank
 *     readable. Bunkerings of different fuel types never contend with each other - each fuel type has its own
 *     family, so their colors are already distinct by hue.
 *
 * Assignment is two-pass, per fuel type, so contention is contained: any bunkering whose preferred slot no other
 * visible bunkering of the same fuel type wants is guaranteed to keep it, meaning an unrelated bunkering appearing
 * or disappearing cannot recolor it. Only genuine contenders (same fuel type, same slot) can move, and the winner
 * is picked deterministically (by guid) so every client resolves it identically.
 *
 * Residual limitation: when two bunkerings of the same fuel type do contend, the guid comparison decides the
 * winner, so a newly added bunkering can still displace an existing one of that fuel type. Because preferred
 * slots now come from a sequential per-fuel-type counter rather than a hash, contention is rare - but eliminating
 * it entirely would require persisting a dedicated color index per bunkering server-side.
 *
 * @param bunkeringGuids the *visible* guids (see getVisibleBunkeringGuids), not every guid in BunkerContext.
 * @param bunkerings all loaded bunkerings, used to resolve each visible guid's reference index and fuel type.
 */
export const getBunkeringColorMap = (bunkeringGuids: string[], bunkerings: Bunkering[]): Map<string, string> => {
    const map = new Map<string, string>();
    const uniqueGuids = Array.from(new Set((bunkeringGuids ?? []).filter((guid) => !!guid))).sort((a, b) => a.localeCompare(b));
    const bunkeringByGuid = new Map((bunkerings ?? []).filter((b) => !!b?.bunkeringGuid).map((b) => [b.bunkeringGuid as string, b]));

    const guidsByFamily = new Map<string, string[]>();
    uniqueGuids.forEach((guid) => {
        const familyKey = String(bunkeringByGuid.get(guid)?.fuelType);
        const group = guidsByFamily.get(familyKey);
        if (group) {
            group.push(guid);
        } else {
            guidsByFamily.set(familyKey, [guid]);
        }
    });

    guidsByFamily.forEach((guidsInFamily) => {
        const family = getColorFamily(bunkeringByGuid.get(guidsInFamily[0])?.fuelType);
        const familyLength = family.length;

        const preferredSlotFor = (guid: string): number =>
            getPreferredFamilySlot(bunkeringByGuid.get(guid) ?? ({ bunkeringGuid: guid } as Bunkering));

        // Pass 1: every guid claims its preferred slot; where several claim the same slot, the first by guid
        // order keeps it and the rest are displaced.
        const takenSlots = new Set<number>();
        const displacedGuids: string[] = [];
        guidsInFamily.forEach((guid) => {
            const preferredSlot = preferredSlotFor(guid);
            if (takenSlots.has(preferredSlot)) {
                displacedGuids.push(guid);
                return;
            }
            takenSlots.add(preferredSlot);
            map.set(guid, family[preferredSlot]);
        });

        // Pass 2: displaced guids probe forward from their preferred slot for the next free shade. Once every
        // slot in the family is taken (more visible bunkerings of this fuel type than shades) the preferred slot
        // is reused and the repeat accepted.
        displacedGuids.forEach((guid) => {
            const preferredSlot = preferredSlotFor(guid);
            let slot = preferredSlot;
            for (let offset = 1; offset <= familyLength && takenSlots.has(slot); offset++) {
                slot = (preferredSlot + offset) % familyLength;
            }
            takenSlots.add(slot);
            map.set(guid, family[slot]);
        });
    });

    return map;
};

/**
 * Looks up a bunkering's color, falling back to its hash-preferred family shade when the guid isn't in the map
 * (e.g. tank contents that carry no ROB row in the visible columns). Prevents an uncolored swatch.
 */
export const getBunkeringColor = (
    bunkering: (Pick<Bunkering, 'bunkeringGuid' | 'bunkeringReference' | 'fuelType'> & { colorIndex?: number | null }) | undefined | null,
    colorMap: Map<string, string>
): string => {
    const cached = colorMap.get(bunkering?.bunkeringGuid ?? '');
    if (cached) return cached;
    const family = getColorFamily(bunkering?.fuelType);
    return family[getPreferredFamilySlot(bunkering ?? ({ bunkeringGuid: '' } as unknown as Bunkering)) % family.length];
};
